import { getDocument, GlobalWorkerOptions, RenderingCancelledException, type PDFPageProxy, type RenderTask } from 'pdfjs-dist';
import { debounce } from '@js/shared/utils';
import { SP_MEDIA_QUERY } from '@js/shared/responsiveTermsLink';

GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.js', import.meta.url).toString();

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const DOUBLE_TAP_INTERVAL = 300;
const RESIZE_DEBOUNCE_MS = 200;
const MAX_CONTENT_WIDTH = 1000;
// Keep one viewport height of pages rendered above and below the visible area.
const RENDER_ROOT_MARGIN = '100% 0px';
// 4096 x 4096: the largest canvas iOS Safari renders (64MB RGBA).
const MAX_CANVAS_PIXELS = 16_777_216;

const availableWidthFor = (stage: HTMLElement): number => Math.min(Math.max(stage.clientWidth - 32, 1), MAX_CONTENT_WIDTH);

const effectiveDevicePixelRatio = (cssWidth: number, cssHeight: number): number =>
  Math.min(window.devicePixelRatio || 1, Math.sqrt(MAX_CANVAS_PIXELS / (cssWidth * cssHeight)));

// True when this page was opened as a new tab (via window.open) from a same-origin page.
const hasSameOriginOpener = (): boolean => {
  try {
    return window.opener !== null && window.opener.location.origin === window.location.origin;
  } catch {
    return false;
  }
};

type RenderedPage = {
  page: PDFPageProxy;
  container: HTMLDivElement;
  canvas: HTMLCanvasElement;
  baseScale: number;
  nearViewport: boolean;
  renderTask: RenderTask | null;
};

const renderTermsPdf = async (): Promise<void> => {
  const viewer = document.querySelector<HTMLElement>('[data-terms-pdf-viewer]');
  if (!viewer || viewer.dataset.initialized === 'true') {
    return;
  }
  viewer.dataset.initialized = 'true';

  const pdfUrl = viewer.dataset.pdfUrl;
  const stage = viewer.querySelector<HTMLElement>('[data-pdf-stage]');
  const status = viewer.querySelector<HTMLElement>('[data-pdf-status]');
  const retryButton = viewer.querySelector<HTMLButtonElement>('[data-pdf-retry]');

  if (!pdfUrl || !stage || !status || !retryButton) {
    return;
  }

  let zoom = MIN_ZOOM;
  let renderedPages: RenderedPage[] = [];
  let pendingRenders = 0;
  let lastTapAt = 0;
  let pinchStartDistance: number | null = null;
  let pinchStartZoom = zoom;
  let pinchPreviewZoom = zoom;
  let pinchOriginX = 0;
  let pinchOriginY = 0;
  const documentContainer = document.createElement('div');
  documentContainer.className = 'terms-pdf-document';
  stage.appendChild(documentContainer);

  const updateStageZoomState = (): void => {
    stage.classList.toggle('is-zoomed', zoom > MIN_ZOOM);
  };

  // Sets CSS size only, so released pages still occupy their space.
  const layoutPage = (renderedPage: RenderedPage) => {
    const viewport = renderedPage.page.getViewport({ scale: renderedPage.baseScale * zoom });
    renderedPage.canvas.style.width = `${viewport.width}px`;
    renderedPage.canvas.style.height = `${viewport.height}px`;
    renderedPage.container.style.width = `${viewport.width}px`;
    return viewport;
  };

  // A 0x0 canvas frees its backing store (required on iOS to reclaim memory).
  const releasePage = (renderedPage: RenderedPage): void => {
    renderedPage.renderTask?.cancel();
    renderedPage.renderTask = null;
    renderedPage.canvas.width = 0;
    renderedPage.canvas.height = 0;
  };

  const paintPage = async (renderedPage: RenderedPage): Promise<void> => {
    const viewport = layoutPage(renderedPage);
    if (!renderedPage.nearViewport) {
      releasePage(renderedPage);
      return;
    }

    renderedPage.renderTask?.cancel();
    const context = renderedPage.canvas.getContext('2d');
    if (!context) {
      throw new Error('PDF canvas context is unavailable');
    }

    const devicePixelRatio = effectiveDevicePixelRatio(viewport.width, viewport.height);
    renderedPage.canvas.width = Math.floor(viewport.width * devicePixelRatio);
    renderedPage.canvas.height = Math.floor(viewport.height * devicePixelRatio);
    context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);

    const renderTask = renderedPage.page.render({ canvasContext: context, viewport });
    renderedPage.renderTask = renderTask;
    pendingRenders += 1;
    viewer.dataset.pdfState = 'rendering';
    try {
      await renderTask.promise;
    } catch (error) {
      if (!(error instanceof RenderingCancelledException)) {
        throw error;
      }
    } finally {
      if (renderedPage.renderTask === renderTask) {
        renderedPage.renderTask = null;
      }
      pendingRenders -= 1;
      if (pendingRenders === 0) {
        // Read by VRT specs to wait for rendering.
        viewer.dataset.pdfState = 'ready';
      }
    }
  };

  const renderPagesAtZoom = async (nextZoom: number): Promise<void> => {
    zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, nextZoom));
    updateStageZoomState();
    documentContainer.style.transform = '';
    documentContainer.style.transformOrigin = '';
    await Promise.all(renderedPages.map((renderedPage) => paintPage(renderedPage)));
  };

  const visibilityObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const renderedPage = renderedPages.find((candidate) => candidate.container === entry.target);
      if (!renderedPage || renderedPage.nearViewport === entry.isIntersecting) {
        return;
      }

      renderedPage.nearViewport = entry.isIntersecting;
      paintPage(renderedPage).catch((error: unknown) => console.error('Failed to render terms PDF page', error));
    });
  }, { rootMargin: RENDER_ROOT_MARGIN });

  const getTouchDistance = (touches: TouchList): number => {
    const firstTouch = touches[0];
    const secondTouch = touches[1];
    return Math.hypot(secondTouch.clientX - firstTouch.clientX, secondTouch.clientY - firstTouch.clientY);
  };

  const resetZoom = (): void => {
    if (zoom === MIN_ZOOM && pinchStartDistance === null) {
      return;
    }

    lastTapAt = 0;
    void renderPagesAtZoom(MIN_ZOOM);
  };

  const back = (): void => {
    if (!hasSameOriginOpener()) {
      history.back();
      return;
    }
    window.close();
    window.setTimeout(() => {
      if (!window.closed) {
        history.back();
      }
    }, 100);
  };

  // Only SP opens the terms page in the same tab, so only SP has somewhere to go back to.
  const backButtonWrapper = viewer.querySelector<HTMLElement>('[data-pdf-back-button-wrapper]');
  if (backButtonWrapper?.hasAttribute('data-show-on-sp') && window.matchMedia(SP_MEDIA_QUERY).matches) {
    backButtonWrapper.hidden = false;
  }
  viewer.querySelector<HTMLButtonElement>('[data-pdf-back-button]')?.addEventListener('click', back);

  // Recomputes the fit-to-width baseline for the current viewport and re-renders at that width,
  // so rotating a device or resizing the window keeps the PDF filling the available width.
  const fitToCurrentWidth = async (): Promise<void> => {
    if (renderedPages.length === 0) {
      return;
    }

    const availableWidth = availableWidthFor(stage);
    renderedPages.forEach((renderedPage) => {
      const baseViewport = renderedPage.page.getViewport({ scale: 1 });
      renderedPage.baseScale = availableWidth / baseViewport.width;
    });
    await renderPagesAtZoom(MIN_ZOOM);
  };

  window.addEventListener('resize', debounce(() => void fitToCurrentWidth(), RESIZE_DEBOUNCE_MS));

  stage.addEventListener('touchstart', (event: TouchEvent) => {
    if (event.touches.length !== 2) {
      return;
    }

    event.preventDefault();
    pinchStartDistance = getTouchDistance(event.touches);
    pinchStartZoom = zoom;
    pinchPreviewZoom = zoom;
    const documentRect = documentContainer.getBoundingClientRect();
    const firstTouch = event.touches[0];
    const secondTouch = event.touches[1];
    const midpointX = (firstTouch.clientX + secondTouch.clientX) / 2;
    const midpointY = (firstTouch.clientY + secondTouch.clientY) / 2;
    pinchOriginX = midpointX - documentRect.left;
    pinchOriginY = midpointY - documentRect.top;
    documentContainer.style.transformOrigin = `${pinchOriginX}px ${pinchOriginY}px`;
  }, { passive: false });

  stage.addEventListener('touchmove', (event: TouchEvent) => {
    if (event.touches.length !== 2 || !pinchStartDistance) {
      return;
    }

    event.preventDefault();
    const pinchRatio = getTouchDistance(event.touches) / pinchStartDistance;
    pinchPreviewZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, pinchStartZoom * pinchRatio));
    const previewRatio = pinchPreviewZoom / zoom;
    documentContainer.style.transform = `scale(${previewRatio})`;
  }, { passive: false });

  stage.addEventListener('touchend', (event: TouchEvent) => {
    if (pinchStartDistance && event.touches.length < 2) {
      documentContainer.style.transform = '';
      void renderPagesAtZoom(pinchPreviewZoom);
      pinchStartDistance = null;
      return;
    }

    if (event.changedTouches.length !== 1) {
      return;
    }

    const now = Date.now();
    if (now - lastTapAt <= DOUBLE_TAP_INTERVAL) {
      event.preventDefault();
      resetZoom();
      return;
    }
    lastTapAt = now;
  }, { passive: false });

  stage.addEventListener('dblclick', (event: MouseEvent) => {
    event.preventDefault();
    resetZoom();
  });

  retryButton.addEventListener('click', () => {
    viewer.dataset.initialized = 'false';
    stage.replaceChildren();
    void renderTermsPdf();
  });

  try {
    const pdf = await getDocument(pdfUrl).promise;
    const availableWidth = availableWidthFor(stage);

    renderedPages = await Promise.all(Array.from({ length: pdf.numPages }, async (_, index) => {
      const page = await pdf.getPage(index + 1);
      const baseViewport = page.getViewport({ scale: 1 });
      const baseScale = availableWidth / baseViewport.width;
      const canvas = document.createElement('canvas');
      const container = document.createElement('div');
      container.className = 'terms-pdf-page';
      canvas.className = 'terms-pdf-canvas';
      canvas.setAttribute('aria-label', `利用規約 ${index + 1}ページ目`);
      container.appendChild(canvas);
      documentContainer.appendChild(container);
      return { page, container, canvas, baseScale, nearViewport: false, renderTask: null };
    }));

    renderedPages.forEach((renderedPage) => {
      layoutPage(renderedPage);
      visibilityObserver.observe(renderedPage.container);
    });
    status.hidden = true;
  } catch (error) {
    console.error('Failed to load terms PDF', error);
    status.textContent = 'PDFを読み込めませんでした。';
    status.hidden = false;
    retryButton.hidden = false;
  }
};

document.addEventListener('turbo:load', () => void renderTermsPdf());
document.addEventListener('DOMContentLoaded', () => void renderTermsPdf());
